import type { GetCurrentMemberFn } from '@/features/iam'
import { action, computed } from 'mobx'
import { ModalViewModel } from '@/infra/ModalViewModel'
import { type Job } from '@/features/jobs/Job'

/**
 * A service item as the dialog lists it.
 */
export interface ServiceItem {
  name: string
  quantity: number
  clientPrice?: number
  proPrice?: number
  courtesyQuantity?: number
}

/**
 * View model for the dialog listing a job's service items.
 */
export class ServiceItemListDialogViewModel {
  readonly modal = new ModalViewModel()

  constructor(
    private readonly _job: Job,
    private readonly _getCurrentMember: GetCurrentMemberFn,
  ) {}

  /**
   * Returns a list of service items.
   */
  @computed
  get serviceItems(): ServiceItem[] {
    return this._job.jobScopeItems
      .filter((item) => item.bundleId === null)
      .map((item) => ({
        name: item.name,
        quantity: item.quantity,
        clientPrice: item.price,
        proPrice: item.proPrice,
        courtesyQuantity: item.courtesyQuantity,
      }))
  }

  /**
   * Determines whether we can see pricing in the `ServiceItemList` modal.
   */
  @computed
  get canSeePriceAsAdmin() {
    return this._getCurrentMember(this._job.workspace.id).permissions
      .adminViewPricing
  }

  /**
   * Determines whether we can see the pro payout in the `ServiceItemList` modal.
   */
  @computed
  get canSeeProPayoutAsAdmin() {
    return this._getCurrentMember(this._job.workspace.id).permissions
      .adminProCutView
  }

  /**
   * Shows the dialog.
   */
  @action.bound
  show() {
    this.modal.show()
  }
}
